import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "../components/Layout";
import api, { today, formatApiError, hasWrite, getRoles } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BankAccountsManager } from "../components/BankAccounts";
import { BranchesManager } from "../components/Branches";
import { Save, Wallet, Hash, Image as ImageIcon, Trash2, Tags, Plus, Pencil, Check, X, Lock, Unlock } from "lucide-react";
import { toast } from "sonner";

const field = "w-full h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const label = "block text-sm font-medium mb-1.5";

function fyLabel() {
  const d = new Date();
  const y = d.getFullYear();
  const start = d.getMonth() >= 3 ? y : y - 1;
  return `${String(start).slice(2)}-${String(start + 1).slice(2)}`;
}

export default function Settings() {
  const { user } = useAuth();
  const canWrite = hasWrite(user);
  const [settings, setSettings] = useState(null);
  const [ob, setOb] = useState({ opening_balance: "", opening_date: today() });
  const [ptypes, setPtypes] = useState([]);
  const [newType, setNewType] = useState("");
  const [renaming, setRenaming] = useState(null);
  const [renameVal, setRenameVal] = useState("");

  const loadTypes = async () => setPtypes((await api.get("/receipt-party-types")).data || []);
  const addType = async () => {
    const n = newType.trim();
    if (!n) return;
    try { await api.post("/receipt-party-types", { name: n }); setNewType(""); toast.success("Party type added"); loadTypes(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveRename = async (old) => {
    const n = renameVal.trim();
    if (!n || n === old) { setRenaming(null); return; }
    try { await api.put(`/receipt-party-types/${encodeURIComponent(old)}`, { new_name: n }); setRenaming(null); toast.success("Party type renamed"); loadTypes(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delType = async (name) => {
    if (!window.confirm(`Remove party type "${name}"? Existing records keep their label.`)) return;
    try { await api.delete(`/receipt-party-types/${encodeURIComponent(name)}`); toast.success("Party type removed"); loadTypes(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const load = async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
    setOb({ opening_balance: data.opening_balance || "", opening_date: data.opening_date || today() });
  };
  useEffect(() => { load(); loadTypes(); }, []);

  const saveOpening = async () => {
    try {
      await api.post("/settings/opening-balance", {
        opening_balance: parseFloat(ob.opening_balance) || 0, opening_date: ob.opening_date,
      });
      toast.success("Opening balance saved");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const lockOpening = async (unlock) => {
    if (!window.confirm(unlock ? "Unlock the opening balance so it can be changed?" : "Lock the opening balance? After this only the superadmin can unlock it.")) return;
    try {
      const { data } = await api.post(`/settings/opening-balance/${unlock ? "unlock" : "lock"}`);
      setSettings(data);
      toast.success(unlock ? "Opening balance unlocked" : "Opening balance locked");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleFy = async () => {
    try {
      const { data } = await api.post("/settings/invoice-config", { invoice_fy_reset: !settings.invoice_fy_reset });
      setSettings(data);
      toast.success(data.invoice_fy_reset ? "Yearly reset enabled — numbers restart each April" : "Yearly reset disabled");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (f.size > 800 * 1024) return toast.error("Image too large. Use one under 800KB.");
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/settings/logo", { logo: reader.result });
        toast.success("Logo saved — it will appear on receipts & vouchers");
        load();
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };
    reader.readAsDataURL(f);
  };

  const removeLogo = async () => {
    try {
      await api.delete("/settings/logo");
      toast.success("Logo removed");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Layout>
      <div className="p-8 max-w-3xl mx-auto">
        <PageHeader title="Settings" subtitle={settings?.name} />

        {settings && (
          <div className="space-y-6">
            {getRoles(user).includes("admin") && <BranchesManager />}
            {getRoles(user).includes("admin") && <BankAccountsManager />}
            <div className="rounded-xl border border-border bg-card p-6 animate-fade-up">
              <div className="flex items-center gap-2 font-display text-xl font-semibold mb-1">
                <Wallet size={18} className="text-accent" /> Cash Opening Balance
                {settings.opening_locked && <span data-testid="opening-locked-badge" className="ml-2 inline-flex items-center gap-1 text-[11px] font-bold uppercase px-2 py-0.5 rounded bg-accent/15 text-accent"><Lock size={11} /> Locked</span>}
              </div>
              <p className="text-sm text-muted-foreground mb-4">Set your starting cash in hand for the first day. The cash book builds on this.{settings.opening_locked ? ` Locked${settings.opening_locked_by ? ` by ${settings.opening_locked_by}` : ""} — only the superadmin can unlock it.` : ""}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className={label}>Opening balance (₹)</label>
                  <input data-testid="opening-balance" type="number" className={field} value={ob.opening_balance} disabled={!canWrite || settings.opening_locked} onChange={(e) => setOb({ ...ob, opening_balance: e.target.value })} /></div>
                <div><label className={label}>As of date</label>
                  <input data-testid="opening-date" type="date" className={`${field} num`} value={ob.opening_date} disabled={!canWrite || settings.opening_locked} onChange={(e) => setOb({ ...ob, opening_date: e.target.value })} /></div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {canWrite && !settings.opening_locked && <button data-testid="save-opening" onClick={saveOpening} className="h-10 px-5 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 hover:bg-primary/90 transition-colors"><Save size={15} /> Save opening balance</button>}
                {!settings.opening_locked && (getRoles(user).includes("admin") || user?.platform_role === "superadmin") && <button data-testid="lock-opening" onClick={() => lockOpening(false)} className="h-10 px-5 rounded-md border border-input text-sm font-medium flex items-center gap-2 hover:bg-muted transition-colors"><Lock size={15} /> Lock opening balance</button>}
                {settings.opening_locked && user?.platform_role === "superadmin" && <button data-testid="unlock-opening" onClick={() => lockOpening(true)} className="h-10 px-5 rounded-md border border-input text-sm font-medium flex items-center gap-2 hover:bg-muted transition-colors"><Unlock size={15} /> Unlock (superadmin)</button>}
              </div>
              {(settings.opening_change_history || []).length > 0 && (
                <div data-testid="opening-change-history" className="mt-5 border-t border-border pt-3">
                  <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">Change history</div>
                  {[...settings.opening_change_history].reverse().map((h, i) => {
                    const amt = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;
                    return (
                      <div key={`${h.at}-${h.field || ""}-${i}`} data-testid={`change-history-${i}`} className="text-xs py-1.5 border-b border-border/60 last:border-0">
                        <div className="flex flex-wrap justify-between gap-2">
                          <span><b>{h.by}</b> <span className="text-muted-foreground">({h.role})</span></span>
                          <span className="num text-muted-foreground">{new Date(h.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
                        </div>
                        {Number(h.old_amount || 0) !== Number(h.new_amount || 0) && <div className="num">Amount: <span className="line-through text-muted-foreground">{amt(h.old_amount)}</span> → <b>{amt(h.new_amount)}</b></div>}
                        {(h.old_date || "") !== (h.new_date || "") && <div className="num">As of: <span className="line-through text-muted-foreground">{h.old_date || "—"}</span> → <b>{h.new_date || "—"}</b></div>}
                      </div>
                    );
                  })}
                </div>
              )}
              {(settings.opening_lock_history || []).length > 0 && (
                <div data-testid="opening-lock-history" className="mt-5 border-t border-border pt-3">
                  <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">Lock history</div>
                  {[...settings.opening_lock_history].reverse().map((h, i) => (
                    <div key={`${h.at}-${h.action || ""}`} data-testid={`lock-history-${i}`} className="flex flex-wrap justify-between gap-2 text-xs py-1 border-b border-border/60 last:border-0">
                      <span className="flex items-center gap-1.5">
                        {h.action === "locked" ? <Lock size={12} className="text-accent" /> : <Unlock size={12} className="text-[hsl(var(--positive))]" />}
                        <b className="capitalize">{h.action}</b> by {h.by} <span className="text-muted-foreground">({h.role})</span>
                      </span>
                      <span className="num text-muted-foreground">{new Date(h.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })} · ₹{Number(h.amount || 0).toLocaleString("en-IN")}{h.as_of ? ` as of ${h.as_of}` : ""}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-border bg-card p-6 animate-fade-up">
              <div className="flex items-center gap-2 font-display text-xl font-semibold mb-1"><ImageIcon size={18} className="text-accent" /> Company Logo</div>
              <p className="text-sm text-muted-foreground mb-4">Upload your logo (PNG/JPG, under 800KB). It prints at the top of every payment receipt and voucher PDF.</p>
              <div className="flex items-center gap-5 flex-wrap">
                <div data-testid="logo-preview" className="h-20 w-40 rounded-md border border-border bg-muted/40 flex items-center justify-center overflow-hidden">
                  {settings.logo
                    ? <img src={settings.logo} alt="Company logo" className="max-h-full max-w-full object-contain" />
                    : <span className="text-xs text-muted-foreground">No logo</span>}
                </div>
                {canWrite && (
                  <div className="flex items-center gap-2">
                    <label data-testid="logo-upload" className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 cursor-pointer hover:bg-primary/90 transition-colors">
                      <ImageIcon size={15} /> {settings.logo ? "Replace logo" : "Upload logo"}
                      <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
                    </label>
                    {settings.logo && (
                      <button data-testid="logo-remove" onClick={removeLogo} className="h-10 px-3 rounded-md border border-input bg-card text-sm flex items-center gap-2 text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={15} /> Remove</button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-border bg-card p-6 animate-fade-up">
              <div className="flex items-center gap-2 font-display text-xl font-semibold mb-1"><Hash size={18} className="text-accent" /> Invoice Numbering</div>
              <p className="text-sm text-muted-foreground mb-4">Invoices follow a serial series. Set the first number when creating your first invoice; the rest auto-increment.</p>
              <div className="grid grid-cols-2 gap-6 text-sm">
                <div><div className="text-muted-foreground">Prefix</div><div className="num font-semibold text-lg">{settings.invoice_prefix}</div></div>
                <div><div className="text-muted-foreground">Next invoice</div><div data-testid="settings-next-invoice" className="num font-semibold text-lg text-accent">{settings.invoice_fy_reset ? `${settings.invoice_prefix}${fyLabel()}-${"1".padStart((settings.invoice_padding >= 2 && settings.invoice_padding <= 8) ? settings.invoice_padding : 5, "0")}` : `${settings.invoice_prefix}${String(settings.invoice_next).padStart((settings.invoice_padding >= 2 && settings.invoice_padding <= 8) ? settings.invoice_padding : 5, "0")}`}</div></div>
              </div>
              <div className="mt-5 pt-4 border-t border-border flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-sm font-medium">Reset numbers every financial year (Apr–Mar)</div>
                  <p className="text-xs text-muted-foreground mt-0.5">When on, new invoices include the year (e.g. {settings.invoice_prefix}{fyLabel()}-0001) and restart at 1 each April. Existing invoices are unchanged.</p>
                </div>
                <button data-testid="toggle-fy-reset" disabled={!canWrite} onClick={toggleFy}
                  className={`shrink-0 relative h-6 w-11 rounded-full transition-colors ${settings.invoice_fy_reset ? "bg-primary" : "bg-muted-foreground/30"} disabled:opacity-40`}>
                  <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform ${settings.invoice_fy_reset ? "translate-x-5" : ""}`} />
                </button>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-card p-6 animate-fade-up">
              <div className="flex items-center gap-2 font-display text-xl font-semibold mb-1"><Tags size={18} className="text-accent" /> Party Types</div>
              <p className="text-sm text-muted-foreground mb-4">Rename or remove the party types used on receipts &amp; vouchers. The built-in <b>Credit</b> type is protected.</p>
              <div className="space-y-2" data-testid="party-types-list">
                {ptypes.map((t) => (
                  <div key={t} data-testid={`party-type-${t}`} className="flex items-center gap-2 bg-muted/40 rounded-md px-3 py-2">
                    {renaming === t ? (
                      <>
                        <input data-testid="party-type-rename-input" autoFocus className={field + " flex-1"} value={renameVal}
                          onChange={(e) => setRenameVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveRename(t)} />
                        <button data-testid="party-type-rename-save" onClick={() => saveRename(t)} className="p-2 rounded-md text-accent hover:bg-accent/10"><Check size={15} /></button>
                        <button onClick={() => setRenaming(null)} className="p-2 rounded-md text-muted-foreground hover:bg-muted"><X size={15} /></button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-sm font-medium">{t}</span>
                        {t.toLowerCase() === "credit" ? (
                          <span className="text-[11px] text-muted-foreground px-2">protected</span>
                        ) : canWrite && (
                          <>
                            <button data-testid={`party-type-rename-${t}`} onClick={() => { setRenaming(t); setRenameVal(t); }} className="p-2 rounded-md text-muted-foreground hover:text-accent hover:bg-accent/10"><Pencil size={14} /></button>
                            <button data-testid={`party-type-delete-${t}`} onClick={() => delType(t)} className="p-2 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10"><Trash2 size={14} /></button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
              {canWrite && (
                <div className="flex gap-2 mt-3">
                  <input data-testid="party-type-new" className={field + " flex-1"} value={newType} placeholder="New party type (e.g. Wholesale)"
                    onChange={(e) => setNewType(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addType()} />
                  <button data-testid="party-type-add" onClick={addType} className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 hover:bg-primary/90 transition-colors"><Plus size={15} /> Add</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
